import React, { useState } from 'react';
import {
    Card,
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Typography,
    Space,
    Popconfirm,
    Tag,
    message,
    Spin,
    Alert,
    Tabs,
} from 'antd';
import {
    SettingOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
    DownloadOutlined,
    UploadOutlined,
    LockOutlined,
    DatabaseOutlined,
    BarChartOutlined,
    ThunderboltOutlined,
    RobotOutlined,
    FileProtectOutlined,
    CreditCardOutlined,
} from '@ant-design/icons';
import { downloadWorkbookFromRows, readFirstSheetAsRows } from '../../utils/workbook';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetSystemSettingsByCategoryQuery,
    useCreateSystemSettingMutation,
    useUpdateSystemSettingMutation,
    useDeleteSystemSettingMutation,
} from '../../services/nestjs-base-api';
import { JobLockingTab } from '../components/job-locking/JobLockingTab';
import { MockDataTab } from '../components/mock-data/MockDataTab';
import { AISystemTab } from '../components/ai-system/AISystemTab';
import { getDisplayName } from '../../utils/elementDisplayNames';
import { BenchmarkingTab } from '../components/benchmarking/BenchmarkingTab';
import { DatabaseBackupTab } from '../components/database-backup/DatabaseBackupTab';
import { RateLimitingTab } from '../components/rate-limiting/RateLimitingTab';
import { LegalSettingsTab } from '../components/legal-settings/LegalSettingsTab';
import { BillingSettingsTab } from '../components/billing-settings/BillingSettingsTab';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface SystemSetting {
    id: number;
    name: string;
    value: string;
    type: string;
    createdAt?: string;
    updatedAt?: string;
}

export const SystemSettingsPage: React.FC = () => {
    const { user } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSetting, setEditingSetting] = useState<SystemSetting | null>(null);
    const [form] = Form.useForm();
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [activeTab, setActiveTab] = useState('system-settings');

    const {
        data: categorizedData,
        isLoading,
        error,
        refetch,
    } = useGetSystemSettingsByCategoryQuery({
        jwtToken: user?.jwtToken || '',
    });

    const [createSetting, { isLoading: isCreating }] = useCreateSystemSettingMutation();
    const [updateSetting, { isLoading: isUpdating }] = useUpdateSystemSettingMutation();
    const [deleteSetting, { isLoading: isDeleting }] = useDeleteSystemSettingMutation();

    // Flatten all settings for the legacy table view
    const settings = React.useMemo(() => {
        if (!categorizedData?.categories) return [];

        return categorizedData.categories.flatMap(category =>
            category.settings.map(setting => ({
                id: setting.name, // Use name as ID since that's what the existing code expects
                name: setting.name,
                value: setting.value,
                type: setting.type,
                createdAt: setting.createdAt ? new Date(setting.createdAt).toISOString() : undefined,
                updatedAt: setting.updatedAt ? new Date(setting.updatedAt).toISOString() : undefined,
            }))
        );
    }, [categorizedData]);

    const handleCreateSetting = () => {
        setEditingSetting(null);
        form.resetFields();
        setIsModalOpen(true);
    };

    const handleEditSetting = (setting: SystemSetting) => {
        setEditingSetting(setting);
        setIsModalOpen(true);
        // Force form reset and then set values
        form.resetFields();
        setTimeout(() => {
            let formattedValue = setting.value;
            
            // Special formatting for ELEMENT_DESCRIPTIONS
            if (setting.name === 'ELEMENT_DESCRIPTIONS') {
                try {
                    const parsed = JSON.parse(setting.value);
                    formattedValue = JSON.stringify(parsed, null, 2);
                } catch {
                    // Use raw value if not valid JSON
                    formattedValue = setting.value;
                }
            }
            
            form.setFieldsValue({
                name: setting.name,
                value: formattedValue,
                type: setting.type,
            });
        }, 50);
    };

    const handleSubmit = async (values: any) => {
        try {
            // Special handling for ELEMENT_DESCRIPTIONS to minify JSON
            if (editingSetting?.name === 'ELEMENT_DESCRIPTIONS' || values.name === 'ELEMENT_DESCRIPTIONS') {
                try {
                    const parsed = JSON.parse(values.value);
                    values.value = JSON.stringify(parsed); // minify for storage
                } catch {
                    message.error('Invalid JSON format. Please check your syntax.');
                    return;
                }
            }
            
            if (editingSetting) {
                await updateSetting({
                    jwtToken: user?.jwtToken || '',
                    settingName: editingSetting.name,
                    payload: { value: values.value },
                }).unwrap();
                message.success('Setting updated successfully');
            } else {
                await createSetting({
                    jwtToken: user?.jwtToken || '',
                    payload: {
                        name: values.name,
                        value: values.value,
                        type: values.type,
                    },
                }).unwrap();
                message.success('Setting created successfully');
            }
            setIsModalOpen(false);
            setEditingSetting(null);
            form.resetFields();
            refetch();
        } catch (error: any) {
            message.error(error?.data?.message || 'Operation failed');
        }
    };

    const handleDeleteSetting = async (settingName: string) => {
        try {
            await deleteSetting({
                jwtToken: user?.jwtToken || '',
                settingName,
            }).unwrap();
            message.success('Setting deleted successfully');
            refetch();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to delete setting');
        }
    };

    const handleExportToExcel = async (setting: SystemSetting) => {
        try {
            // Parse the JSON
            const data = JSON.parse(setting.value);
            
            // Prepare data for Excel in the same format as the uploaded file
            const excelData: any[] = [];
            
            // Add header row
            excelData.push({
                'ELEMENT': 'ELEMENT',
                'MOUSEOVER': 'MOUSEOVER',
                'SIDEBAR': 'SIDEBAR'
            });
            
            // Add data rows from the elements object
            if (data.elements) {
                Object.entries(data.elements).forEach(([elementName, elementData]: [string, any]) => {
                    excelData.push({
                        'ELEMENT': elementName,
                        'MOUSEOVER': (elementData.mouseover || '').toString().trim(),
                        'SIDEBAR': (elementData.sidebar || '').toString().trim()
                    });
                });
            }
            
            // Positional rows — excelData already carries its own header row as
            // the first entry, which is what skipHeader: true relied on.
            const rows = excelData.map((row) => [
                row.ELEMENT,
                row.MOUSEOVER,
                row.SIDEBAR,
            ]);

            // Generate filename with current date
            const date = new Date().toISOString().split('T')[0];
            const filename = `Element_Descriptions_${date}.xlsx`;

            await downloadWorkbookFromRows(filename, [
                { name: 'Sheet1', rows },
            ]);
            
            message.success('Element descriptions exported successfully');
        } catch (error) {
            console.error('Export error:', error);
            message.error('Failed to export element descriptions');
        }
    };

    const handleImportFromExcel = (file: File) => {
        const reader = new FileReader();
        
        reader.onload = async (e) => {
            try {
                const jsonData = await readFirstSheetAsRows(
                    e.target?.result as ArrayBuffer,
                );
                
                // Skip header row and process data
                const elements: { [key: string]: { mouseover: string; sidebar: string } } = {};
                
                for (let i = 1; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (row[0] && row[0] !== 'ELEMENT') { // Skip header if present
                        const elementName = (row[0] || '').toString().trim();
                        const mouseover = (row[1] || '').toString().trim();
                        const sidebar = (row[2] || '').toString().trim();
                        
                        // Only add elements that have a valid name
                        if (elementName) {
                            elements[elementName] = {
                                mouseover,
                                sidebar
                            };
                        }
                    }
                }
                
                // Get existing data to preserve version
                let existingData = { version: '1.0.0' };
                try {
                    const currentValue = form.getFieldValue('value');
                    if (currentValue) {
                        const parsed = JSON.parse(currentValue);
                        if (parsed.version) {
                            existingData.version = parsed.version;
                        }
                    }
                } catch {
                    // Use default version
                }
                
                // Create the new JSON structure
                const newData = {
                    version: existingData.version,
                    elements
                };
                
                // Format and set the form value
                const formattedJson = JSON.stringify(newData, null, 2);
                form.setFieldsValue({ value: formattedJson });
                
                message.success('Element descriptions imported successfully');
            } catch (error) {
                console.error('Import error:', error);
                message.error('Failed to import Excel file. Please ensure it has the correct format.');
            }
        };
        
        reader.onerror = () => {
            message.error('Failed to read file');
        };
        
        reader.readAsArrayBuffer(file);
    };

    const getTypeColor = (type: string) => {
        switch (type.toUpperCase()) {
            case 'BOOLEAN':
                return 'blue';
            case 'INTEGER':
                return 'green';
            case 'STRING':
                return 'orange';
            default:
                return 'default';
        }
    };

    const formatValue = (value: string, type: string) => {
        if (type.toUpperCase() === 'BOOLEAN') {
            return value === 'true' || value === '1' ? 'True' : 'False';
        }
        if (value.length > 50) {
            return value.substring(0, 50) + '...';
        }
        return value;
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: SystemSetting, b: SystemSetting) => a.name.localeCompare(b.name),
            render: (text: string) => (
                <Text strong style={{ fontFamily: 'monospace' }}>
                    {text}
                </Text>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            filters: [
                { text: 'Boolean', value: 'BOOLEAN' },
                { text: 'Integer', value: 'INTEGER' },
                { text: 'String', value: 'STRING' },
            ],
            onFilter: (value: any, record: SystemSetting) => record.type === value,
            render: (type: string) => (
                <Tag color={getTypeColor(type)}>{type}</Tag>
            ),
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
            render: (value: string, record: SystemSetting) => (
                <Text
                    style={{
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        padding: '2px 6px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                        display: 'inline-block',
                        maxWidth: '300px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                    title={value}
                >
                    {formatValue(value, record.type)}
                </Text>
            ),
        },
        {
            title: 'Last Updated',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            sorter: (a: SystemSetting, b: SystemSetting) => {
                const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                return aTime - bTime;
            },
            render: (date: string) => (
                <Text type="secondary">
                    {date ? new Date(date).toLocaleString() : 'N/A'}
                </Text>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: SystemSetting) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditSetting(record)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete System Setting"
                        description="Are you sure you want to delete this setting? This action cannot be undone."
                        onConfirm={() => handleDeleteSetting(record.name)}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            loading={isDeleting}
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (isLoading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert
                    message="Error loading system settings"
                    description="Unable to fetch system settings. Please try again later."
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    // A plain element, NOT an inline component: a component type defined inside
    // render gets a new identity every render, so React unmounted and rebuilt
    // all ~20 category tables on every state change (modal open/close, tab
    // switch, any mutation loading flip). An element reconciles in place.
    const systemSettingsTabContent = (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 24,
                }}
            >
                <div>
                    <Text type="secondary">
                        Manage global system configuration settings organized by category
                    </Text>
                </div>
                <Space>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => refetch()}
                        loading={isLoading}
                    >
                        Refresh
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreateSetting}
                    >
                        Add Setting
                    </Button>
                </Space>
            </div>

            {categorizedData?.categories?.map((category) => (
                <Card
                    key={category.category}
                    id={`settings-category-${category.category.replace(/\s+/g, '-').toLowerCase()}`}
                    title={getDisplayName(category.category)}
                    style={{ marginBottom: 16 }}
                    size="small"
                >
                    <Table
                        columns={columns}
                        dataSource={category.settings.map((setting, index) => ({
                            id: index + 1, // Use numeric ID
                            name: setting.name,
                            value: setting.value,
                            type: setting.type,
                            createdAt: setting.createdAt ? new Date(setting.createdAt).toISOString() : undefined,
                            updatedAt: setting.updatedAt ? new Date(setting.updatedAt).toISOString() : undefined,
                        }))}
                        rowKey="name"
                        pagination={false}
                        scroll={{ x: 800 }}
                        size="small"
                    />
                </Card>
            ))}
        </>
    );

    return (
        <div style={{ padding: '24px' }}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 24,
                }}
            >
                <div>
                    <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                        <SettingOutlined style={{ marginRight: 8 }} />
                        System Settings
                    </Title>
                    <Text type="secondary">
                        Manage system configuration and job scheduling
                    </Text>
                </div>
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                    {
                        key: 'system-settings',
                        label: (
                            <span>
                                <SettingOutlined style={{ marginRight: 8 }} />
                                System Settings
                            </span>
                        ),
                        children: systemSettingsTabContent,
                    },
                    {
                        key: 'billing-settings',
                        label: (
                            <span>
                                <CreditCardOutlined style={{ marginRight: 8 }} />
                                Billing & Stripe
                            </span>
                        ),
                        children: <BillingSettingsTab />,
                    },
                    {
                        key: 'ai-system',
                        label: (
                            <span>
                                <RobotOutlined style={{ marginRight: 8 }} />
                                AI System
                            </span>
                        ),
                        children: <AISystemTab />,
                    },
                    {
                        key: 'rate-limiting',
                        label: (
                            <span>
                                <ThunderboltOutlined style={{ marginRight: 8 }} />
                                Rate Limiting
                            </span>
                        ),
                        children: <RateLimitingTab />,
                    },
                    {
                        key: 'legal-settings',
                        label: (
                            <span>
                                <FileProtectOutlined style={{ marginRight: 8 }} />
                                Legal Settings
                            </span>
                        ),
                        children: <LegalSettingsTab />,
                    },
                    {
                        key: 'job-locking',
                        label: (
                            <span>
                                <LockOutlined style={{ marginRight: 8 }} />
                                Job Locking
                            </span>
                        ),
                        children: <JobLockingTab />,
                    },
                    {
                        key: 'benchmarking',
                        label: (
                            <span>
                                <BarChartOutlined style={{ marginRight: 8 }} />
                                Benchmarking
                            </span>
                        ),
                        children: <BenchmarkingTab />,
                    },
                    {
                        key: 'database-backup',
                        label: (
                            <span style={{ color: '#ff4d4f' }}>
                                <DatabaseOutlined style={{ marginRight: 8 }} />
                                Database Backup
                            </span>
                        ),
                        children: <DatabaseBackupTab onNavigateToSettings={() => {
                            setActiveTab('system-settings');
                            setTimeout(() => {
                                document.getElementById('settings-category-database-backups')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }, 100);
                        }} />,
                    },
                    {
                        key: 'mock-data',
                        label: (
                            <span style={{ color: '#ff4d4f' }}>
                                <DatabaseOutlined style={{ marginRight: 8 }} />
                                Mock Data
                            </span>
                        ),
                        children: <MockDataTab />,
                    },
                ]}
            />

            <Modal
                title={editingSetting ? 'Edit System Setting' : 'Create System Setting'}
                open={isModalOpen}
                onCancel={() => {
                    setIsModalOpen(false);
                    setEditingSetting(null);
                    form.resetFields();
                }}
                footer={null}
                width={600}
                destroyOnClose={true}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    style={{ marginTop: 16 }}
                    key={editingSetting ? editingSetting.id : 'create'}
                >
                    <Form.Item
                        name="name"
                        label="Setting Name"
                        rules={[
                            { required: true, message: 'Please enter a setting name' },
                            {
                                pattern: /^[A-Z][A-Z0-9_]*$/,
                                message: 'Name must be uppercase with underscores (e.g., MY_SETTING)',
                            },
                        ]}
                    >
                        <Input
                            placeholder="e.g., NEW_FEATURE_ENABLED"
                            disabled={!!editingSetting}
                        />
                    </Form.Item>

                    <Form.Item
                        name="type"
                        label="Setting Type"
                        rules={[{ required: true, message: 'Please select a type' }]}
                    >
                        <Select
                            placeholder="Select setting type"
                            disabled={!!editingSetting}
                        >
                            <Select.Option value="BOOLEAN">Boolean</Select.Option>
                            <Select.Option value="INTEGER">Integer</Select.Option>
                            <Select.Option value="STRING">String</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => {
                            const type = getFieldValue('type') || editingSetting?.type;
                            const settingName = getFieldValue('name') || editingSetting?.name;
                            const isElementDescriptions = settingName === 'ELEMENT_DESCRIPTIONS';
                            const isElementVideos = settingName === 'PUBLIC_HYPOTHESIS_ELEMENT_VIDEOS';
                            // Both hold JSON, and a four-row proportional
                            // textarea is a bad place to edit a map by hand.
                            const isJsonSetting = isElementDescriptions || isElementVideos;

                            return (
                                <>
                                    {isElementVideos && (
                                        <Alert
                                            type="info"
                                            showIcon
                                            message="Element explainer videos"
                                            description={
                                                <>
                                                    <div>
                                                        Keys are the <strong>canonical</strong> element names
                                                        (Brand Loyalty, Perceived Quality, Willingness to Pay a
                                                        Premium, Associations, Brand Attachment), not display
                                                        names. Values are a bare YouTube video id, never a URL.
                                                        An element with no entry simply shows no video.
                                                    </div>
                                                    <pre style={{ margin: '8px 0 0', fontSize: 12 }}>
{`{
  "Perceived Quality": "OHwyhbvnVLs",
  "Brand Loyalty": { "id": "abc123XYZ", "title": "Custom heading" }
}`}
                                                    </pre>
                                                </>
                                            }
                                            style={{ marginBottom: 12 }}
                                        />
                                    )}
                                    {isElementDescriptions && (
                                        <Alert
                                            type="warning"
                                            showIcon
                                            message="JSON keys are load-bearing"
                                            description='Do NOT rename the JSON keys. They are canonical element names ("Alignment Depth", "Distribution Intensity", "Standardised NPS", …) that the dashboard visualizations match against for tooltips — renaming a key to its display name (Touchpoints, Availability, Advocacy Intent) breaks those lookups. The description text inside the values is safe to edit freely; old element names in any text sent to the AI are rewritten to the display names automatically.'
                                            style={{ marginBottom: 12 }}
                                        />
                                    )}
                                <Form.Item
                                    name="value"
                                    label="Setting Value"
                                    rules={[{ required: true, message: 'Please enter a value' }]}
                                >
                                    {type === 'BOOLEAN' ? (
                                        <Select placeholder="Select boolean value">
                                            <Select.Option value="true">True</Select.Option>
                                            <Select.Option value="false">False</Select.Option>
                                        </Select>
                                    ) : type === 'INTEGER' ? (
                                        <Input
                                            type="number"
                                            placeholder="Enter integer value"
                                        />
                                    ) : (
                                        <TextArea
                                            rows={isJsonSetting ? 15 : 4}
                                            placeholder={isJsonSetting ? "Enter JSON" : "Enter string value"}
                                            style={isJsonSetting ? {
                                                fontFamily: 'monospace',
                                                fontSize: '13px',
                                                backgroundColor: '#f5f5f5',
                                                border: '1px solid #d9d9d9',
                                                borderRadius: '6px'
                                            } : undefined}
                                        />
                                    )}
                                </Form.Item>
                                </>
                            );
                        }}
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
                            {editingSetting && editingSetting.name === 'ELEMENT_DESCRIPTIONS' && (
                                <>
                                    <Space wrap>
                                        <Button
                                            icon={<DownloadOutlined />}
                                            onClick={() => handleExportToExcel(editingSetting)}
                                            size="small"
                                        >
                                            Export
                                        </Button>
                                        <Button
                                            icon={<UploadOutlined />}
                                            onClick={() => fileInputRef.current?.click()}
                                            size="small"
                                        >
                                            Import
                                        </Button>
                                    </Space>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".xlsx,.xls"
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                handleImportFromExcel(file);
                                                // Reset the input so the same file can be selected again
                                                e.target.value = '';
                                            }
                                        }}
                                    />
                                </>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Space>
                                    <Button onClick={() => setIsModalOpen(false)}>
                                        Cancel
                                    </Button>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={isCreating || isUpdating}
                                    >
                                        {editingSetting ? 'Update Setting' : 'Create Setting'}
                                    </Button>
                                </Space>
                            </div>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};